import { Box, Typography, Chip, Theme } from "@mui/material";

export interface SelectionItem {
  value?: string;
  label?: string;
  Value?: string;
  Title?: string;
  [key: string]: unknown; // Allow additional properties
}

export interface SelectionColumnProps {
  title: string;
  data: SelectionItem[];
  selectedItems: string[];
  onSelectionChange: (value: string) => void;
  valueField?: string;
  titleField?: string;
  layoutMode?: "flex" | "twoPerRow" | "dynamic";
  isNarrowColumn?: boolean;
  showTitleDivider?: boolean;
  allowMultipleSelection?: boolean;
  customWidth?: string;
}

export const SelectionColumn = ({
  title,
  data,
  selectedItems,
  onSelectionChange,
  valueField = "Value",
  titleField = "Title",
  layoutMode = "flex",
  isNarrowColumn = false,
  showTitleDivider = true,
  allowMultipleSelection = false,
  customWidth,
}: SelectionColumnProps) => {
  // Prepare chip data with selection status
  const chipData = data.map((item, index) => {
    // Improved field mapping with more fallback options
    const value =
      item[valueField] ||
      item.Value ||
      item.ID ||
      item.id ||
      item.Code ||
      index.toString();

    const label = String(
      item[titleField] ||
        item.Title ||
        item.Name ||
        item.HeaderText ||
        item.Description ||
        item.Text ||
        value ||
        "",
    );

    const isSelected = selectedItems.includes(value.toString());

    return {
      value: value.toString(),
      label,
      isSelected,
      originalIndex: index,
    };
  });

  // Sort chips: selected first, then unselected
  const sortedChips = chipData.sort((a, b) => {
    if (a.isSelected && !b.isSelected) return -1;
    if (!a.isSelected && b.isSelected) return 1;
    return a.originalIndex - b.originalIndex; // Maintain original order within groups
  });

  // Separate selected and unselected chips
  const selectedChips = sortedChips.filter((chip) => chip.isSelected);
  const unselectedChips = sortedChips.filter((chip) => !chip.isSelected);

  // Determine container sx and chip sx based on layout mode
  const getContainerSx = () => {
    switch (layoutMode) {
      case "twoPerRow":
        return {
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "4px",
          padding: "8px",
          justifyItems: "center",
          alignItems: "start",
        };
      case "dynamic":
        return {
          display: "flex",
          flexWrap: "wrap" as const,
          gap: "6px",
          padding: "8px",
          justifyContent: "center",
          alignItems: "flex-start",
        };
      default:
        return {
          display: "flex",
          flexWrap: "wrap" as const,
          gap: "8px",
          padding: "8px",
          justifyContent: "center",
        };
    }
  };

  const getChipSx = () => {
    return layoutMode === "twoPerRow"
      ? {
          fontSize: "14px",
          height: "40px",
          minWidth: "60px",
          maxWidth: "120px",
          cursor: "pointer",
          padding: "8px 12px",
          borderRadius: "20px",
          transition: "all 0.2s ease",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)",
          },
          "&:active": {
            transform: "translateY(0)",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
          },
        }
      : {
          fontSize: "16px",
          height: "48px",
          minWidth: "80px",
          cursor: "pointer",
          padding: "12px 16px",
          borderRadius: "24px",
          transition: "all 0.2s ease",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)",
          },
          "&:active": {
            transform: "translateY(0)",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
          },
        };
  };

  const columnContainerSx = isNarrowColumn
    ? {
        width: customWidth || "15%",
        height: "100%",
        marginLeft: "0.5%",
        marginRight: "0.5%",
        background: (theme: Theme) =>
          theme.palette.billetYard.backgrounds.gradient,
        border: (theme: Theme) =>
          `1px solid ${theme.palette.billetYard.borders.light}`,
        borderRadius: "12px",
        padding: "14px",
        overflowY: "auto",
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }
    : {
        width: customWidth || "17%",
        height: "100%",
        marginLeft: "0.5%",
        marginRight: "0.5%",
        background: (theme: Theme) =>
          theme.palette.billetYard.backgrounds.gradient,
        border: (theme: Theme) =>
          `1px solid ${theme.palette.billetYard.borders.light}`,
        borderRadius: "12px",
        padding: "16px",
        overflowY: "auto",
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      };

  return (
    <Box sx={columnContainerSx}>
      <Typography
        sx={{
          fontSize: "20px",
          fontWeight: 700,
          color: (theme) => theme.palette.text.primary,
          marginBottom: "20px",
          paddingBottom: "16px",
          borderBottom: (theme) =>
            `2px solid ${theme.palette.billetYard.borders.light}`,
          textAlign: "center",
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>

      {/* Divider line below title - only show when there are selected items */}
      {showTitleDivider && selectedChips.length > 0 && (
        <Box
          sx={{
            width: "100%",
            height: "1px",
            backgroundColor: (theme) => theme.palette.billetYard.borders.light,
            marginBottom: "8px", // Reduced from 16px to 8px for tighter spacing
          }}
        />
      )}

      <Box sx={getContainerSx()}>
        {/* Render selected chips first */}
        {selectedChips.map((chip, index) => (
          <Chip
            key={`selected-${chip.value}`}
            label={chip.label}
            onClick={() => onSelectionChange(chip.value)}
            variant="outlined"
            sx={{
              ...getChipSx(),
              backgroundColor: (theme) =>
                theme.palette.billetYard.selected.background,
              color: (theme) => theme.palette.text.primary,
              fontWeight: 600,
              border: (theme) =>
                `1px solid ${theme.palette.billetYard.selected.border}`,
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.billetYard.selected.hover,
                transform: "translateY(-1px)",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)",
              },
              // Center single selected chip in grid layout
              gridColumn:
                layoutMode === "twoPerRow" && selectedChips.length === 1
                  ? "1 / -1"
                  : undefined,
            }}
          />
        ))}

        {/* Visual separator between selected and unselected if there are both */}
        {selectedChips.length > 0 &&
          unselectedChips.length > 0 &&
          selectedChips.length + unselectedChips.length > 1 && (
            <Box
              sx={{
                width: "100%",
                height: "1px",
                backgroundColor: (theme) =>
                  theme.palette.billetYard.borders.light,
                margin: "8px 0",
                gridColumn: layoutMode === "twoPerRow" ? "1 / -1" : undefined,
                flexBasis: layoutMode === "dynamic" ? "100%" : "auto",
              }}
            />
          )}

        {/* Render unselected chips */}
        {unselectedChips.map((chip) => (
          <Chip
            key={`unselected-${chip.value}`}
            label={chip.label}
            onClick={() => onSelectionChange(chip.value)}
            variant="outlined"
            sx={{
              ...getChipSx(),
              backgroundColor: "transparent",
              color: "inherit",
              fontWeight: 400,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              "&:hover": {
                backgroundColor: (theme) => theme.palette.action.hover,
                transform: "translateY(-1px)",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)",
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};
